import React from 'react';
import { DemeterPageType, DemeterTableDefinitionType, UnitOfMeasure } from '../../../../Generated/Raven-Demeter';
import { IProjectionChartProps } from '../../../Components/Charts/Projection/ProjectionChart';
import { IRegionCommoditySelection } from '../../../Components/Navigation/Hooks/useRegionCommodityNavigationHook';
import useLanguage from '../../../Services/Language/useLanguageHook';
import { IWithPageElementProps } from '../DashboardCustomizationDefinitions';

const withProjectionChartFromPageElement = (ProjectionChart: React.ComponentType<IProjectionChartProps>): React.FC<IWithPageElementProps> => {
    const Wrapped: React.FC<IWithPageElementProps> = (props) => {
        if (!props.pageElement) {
            return <> </>;
        }

        const { demeterPageElementGuid, pageType, region, subRegion, commodity, subCommodity, extraParameters, dataFrequency, unitOfMeasure } =
            props.pageElement;
        const [translations] = useLanguage();

        let pageTitle: string;
        let tableDefinitionType: DemeterTableDefinitionType;
        switch (pageType) {
            case DemeterPageType.Imports:
                pageTitle = translations.imports.title;
                tableDefinitionType = DemeterTableDefinitionType.CommodityMonthlyImportsTable;
                break;
            case DemeterPageType.Production:
                pageTitle = translations.production.title;
                tableDefinitionType = DemeterTableDefinitionType.CommodityMonthlyProductionTable;
                break;
            case DemeterPageType.Exports:
                pageTitle = translations.exports.title;
                tableDefinitionType = DemeterTableDefinitionType.CommodityMonthlyExportsTable;
                break;
            case DemeterPageType.BalanceTable:
                pageTitle = translations.balanceTable.title;
                tableDefinitionType = DemeterTableDefinitionType.CommodityYearlyBalanceTable;
                break;
            case DemeterPageType.Stocks:
                pageTitle = translations.stocks.title;
                tableDefinitionType = DemeterTableDefinitionType.CommodityMonthlyStockTable;
                break;
            case DemeterPageType.Domestics:
                pageTitle = translations.domestics.title;
                tableDefinitionType = DemeterTableDefinitionType.CommodityMonthlyDomesticTable;
                break;
            default:
                pageTitle = '';
                tableDefinitionType = DemeterTableDefinitionType.AllFundamentalsAndPrices;
                break;
        }

        const getExtraParameters = () => {
            if (props.widgetOptions?.extraParameters) {
                return props.widgetOptions.extraParameters;
            }

            return extraParameters && Object.keys(extraParameters).length > 0 ? JSON.stringify(extraParameters) : null;
        };

        const regionCommoditySelection = {
            region,
            subRegion,
            commodity: subCommodity ?? commodity,
            extraParameters: getExtraParameters(),
            dataFrequency,
        } as IRegionCommoditySelection;

        const chartProps: IProjectionChartProps = {
            title: pageTitle,
            customTitle: props.customTitle,
            tableDefinitionType,
            regionCommoditySelection,
            aggregateRegions: [],
            unitOfMeasure: unitOfMeasure ?? UnitOfMeasure.MetricTon,
            testId: `dashboard-projection-chart-${demeterPageElementGuid}`,
            dragIndicatorIcon: props.dragIndicatorIcon,
            isInDashboardEditMode: props.isInDashboardEditMode,
            maxWidthHeightRatio: props.maxWidthHeightRatio,
            maximizeMinimizeButton: props.maximizeMinimizeButton,
            handleMoreOptionsSelection: props.handleMoreOptionsSelection,
            track: {
                eventCategory: 'Dashboard',
                eventAction: 'ProjectionChart',
                eventValue: { tableDefinitionType, regionCommoditySelection },
            },
        };

        return <ProjectionChart {...chartProps} />;
    };

    return Wrapped;
};

export default withProjectionChartFromPageElement;
